import { AlertTriangle, Armchair, Lock, Unlock } from 'lucide-react';
import type { SeatingConflict, SeatingState } from '@peles/shared';
import { Popover } from '../common/Popover';

export interface SeatingPlanControlsProps {
  /** The plan on screen's seating; nothing is shown until it has loaded. */
  state: SeatingState | undefined;
  onMakePrimary: () => void;
  onToggleLock: (locked: boolean) => void;
  busy?: boolean;
  /** Show the words beside the icons (off in focus mode). */
  showLabels?: boolean;
  /** Makes each conflict a link: to its table, or the first guest it names. */
  onConflictSelect?: (conflict: SeatingConflict) => void;
  /**
   * The office's controls (choosing the seating plan, locking it). Off in the
   * client portal, where the plan's state is shown but not changed.
   */
  office?: boolean;
}

/**
 * The seating controls for the plan on screen: which of the event's plans the
 * guest list, exports and the seat finder read ("Seating plan"), locking its
 * seating for printing, and what about it needs a look.
 */
export function SeatingPlanControls({
  state,
  onMakePrimary,
  onToggleLock,
  busy = false,
  showLabels = true,
  onConflictSelect,
  office = true,
}: SeatingPlanControlsProps) {
  if (!state) return null;
  const { plan, conflicts } = state;
  const locked = !!plan.seatingLockedAt;
  const button = 'flex items-center gap-1 px-3 py-2 border text-sm rounded-md disabled:opacity-50';

  return (
    <div className="flex items-center gap-2" data-testid="seating-plan-controls">
      {plan.isPrimarySeating ? (
        <span
          className="flex items-center gap-1 px-3 py-2 text-sm rounded-md bg-primary-50 text-primary-700"
          title="The guest list, exports and the seat finder read this plan's seats"
          data-testid="seating-primary-badge"
        >
          <Armchair size={14} />
          {showLabels && 'Seating plan'}
        </span>
      ) : (
        office && (
          <button
            type="button"
            onClick={onMakePrimary}
            disabled={busy}
            className={`${button} border-gray-300 text-gray-700 hover:bg-gray-50`}
            title="Make this the plan the guest list, exports and the seat finder read"
            data-testid="seating-make-primary"
          >
            <Armchair size={14} />
            {showLabels && 'Use for seating'}
          </button>
        )
      )}
      {office ? (
        <button
          type="button"
          onClick={() => onToggleLock(!locked)}
          disabled={busy}
          aria-pressed={locked}
          className={`${button} ${
            locked
              ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
          title={
            locked
              ? 'Seating is locked for printing — clients cannot change it. Unlock to allow changes.'
              : 'Lock seating for printing, so nobody but the office changes it'
          }
          data-testid="seating-lock-toggle"
        >
          {locked ? <Lock size={14} /> : <Unlock size={14} />}
          {showLabels && (locked ? 'Seating locked' : 'Lock seating')}
        </button>
      ) : (
        locked && (
          <span
            className="flex items-center gap-1 px-3 py-2 text-sm rounded-md bg-amber-50 text-amber-800"
            data-testid="seating-locked-badge"
          >
            <Lock size={14} />
            {showLabels && 'Seating locked'}
          </span>
        )
      )}
      {conflicts.length > 0 && (
        <Popover
          ariaLabel="Seating to check"
          align="end"
          panelClassName="w-96"
          trigger={(props) => (
            <button
              type="button"
              {...props}
              className={`${button} border-amber-300 text-amber-800 hover:bg-amber-50`}
              data-testid="seating-conflicts-btn"
            >
              <AlertTriangle size={14} />
              {conflicts.length}
              {showLabels && ` to check`}
            </button>
          )}
        >
          {(close) => (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Seating to check</h3>
              <ul className="space-y-2 max-h-80 overflow-y-auto" data-testid="seating-conflicts">
                {conflicts.map((conflict, i) => (
                  <li
                    key={`${conflict.kind}-${i}`}
                    className="text-sm text-gray-700 flex gap-2 items-start"
                  >
                    <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                    {onConflictSelect ? (
                      <button
                        type="button"
                        className="text-left hover:underline"
                        onClick={() => {
                          onConflictSelect(conflict);
                          close();
                        }}
                      >
                        {conflict.message}
                      </button>
                    ) : (
                      <span>{conflict.message}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Popover>
      )}
    </div>
  );
}
